import { useCallback, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import z from 'zod'
import type { DocumentCollectionIcon } from '@/graphql/schema'
import { defaultDocumentCollectionIcon } from '@/entities/document-collection/document-collection-icons'
import { useCreateDocumentCollectionMutation } from '@/graphql/mutations/createDocumentCollectionMutation.gql.generated'
import { DocumentCollectionsDocument } from '@/graphql/queries/documentCollectionsQuery.gql.generated'
import { useAppForm } from '@/shared/form/use-app-form'

const schema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'documentCollections:collectionNameRequired'),
    icon: z.custom<DocumentCollectionIcon>(),
    description: z.string(),
    createFirstField: z.boolean(),
    firstFieldName: z.string(),
  })
  .superRefine((value, context) => {
    if (value.createFirstField && value.firstFieldName.trim().length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'documentCollections:firstFieldRequired',
        path: ['firstFieldName'],
      })
    }
  })

export type DocumentCollectionFormValues = z.infer<typeof schema>

export function useAddDocumentCollectionSheetController({
  onOpenChange,
  open: controlledOpen,
}: {
  onOpenChange?: (open: boolean) => void
  open?: boolean
}) {
  const { t } = useTranslation('documentCollections')
  const navigate = useNavigate()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const open = controlledOpen ?? uncontrolledOpen
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(nextOpen)
      if (!nextOpen) setSubmitError(null)
      onOpenChange?.(nextOpen)
    },
    [controlledOpen, onOpenChange],
  )
  const [createDocumentCollection] = useCreateDocumentCollectionMutation({
    refetchQueries: [{ query: DocumentCollectionsDocument }],
  })
  const form = useAppForm({
    defaultValues: {
      name: '',
      icon: defaultDocumentCollectionIcon,
      description: '',
      createFirstField: false,
      firstFieldName: '',
    },
    validators: {
      onBlur: schema as never,
      onChange: schema as never,
      onSubmit: schema as never,
    },
    onSubmit: async ({ value }) => {
      try {
        setSubmitError(null)
        const result = await createDocumentCollection({
          variables: {
            input: {
              name: value.name.trim(),
              description: value.description.trim() || null,
              icon: value.icon,
              ...(value.createFirstField && {
                firstFieldName: value.firstFieldName.trim(),
              }),
            },
          },
        })
        const collection = result.data?.createDocumentCollection
        if (!collection) {
          throw new Error(t('documentCollections:addCollectionError'))
        }
        await navigate({
          to: '/document-collection/$id',
          params: { id: collection.id },
        })
        form.reset()
        handleOpenChange(false)
      } catch (error) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : t('documentCollections:addCollectionError'),
        )
      }
    },
  })

  return { form, handleOpenChange, open, submitError }
}
